import React, { useEffect, useState } from 'react';
import './TrabalheConosco.css';
import { SiteHeader, SiteFooter } from '../components/Shared';
import { getStaffApplyInfo, submitStaffApply } from '../api';

function maskPhone(value) {
  const digits = value.replace(/\D/g, '').substring(0, 11);

  if (digits.length > 10) {
    return digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
  } else if (digits.length > 6) {
    return digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
  } else if (digits.length > 2) {
    return digits.replace(/^(\d{2})(\d{0,5}).*/, '($1) $2');
  } else if (digits.length > 0) {
    return digits.replace(/^(\d{0,2}).*/, '($1');
  }

  return digits;
}

function isValidPhone(value) {
  const digits = value.replace(/\D/g, '');
  return /^[1-9][1-9](9\d{8}|\d{8})$/.test(digits);
}

const FACT_ICONS = {
  horario: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  diaria: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  dias: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  ),
  frequencia: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 7h-9M14 17H5M17 4l3 3-3 3M8 14l-3 3 3 3" />
    </svg>
  ),
  local: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 21s-7-7.2-7-12a7 7 0 0 1 14 0c0 4.8-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
};

function TrabalheConosco() {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneHelp, setPhoneHelp] = useState('Usaremos este número para falar sobre as próximas datas.');
  const [email, setEmail] = useState('');
  const [availability, setAvailability] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getStaffApplyInfo()
      .then(setInfo)
      .finally(() => setLoadingInfo(false));
  }, []);

  function toggleDay(day) {
    setAvailability((current) =>
      current.includes(day) ? current.filter((d) => d !== day) : [...current, day]
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = [];
    if (!name.trim()) errors.push('Preencha o campo "Nome completo".');
    if (!phone.trim() || !isValidPhone(phone)) errors.push('Telefone inválido — informe o DDD e o número.');
    if (!email.trim()) errors.push('Preencha o campo "E-mail".');
    if (availability.length === 0) errors.push('Selecione ao menos um dia de disponibilidade.');

    if (errors.length > 0) {
      setResult({ status: 'error', errors });
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const response = await submitStaffApply({ name, phone, email, availability });
      setResult(response);
    } catch (e) {
      setResult({ status: 'error', errors: ['Não foi possível enviar. Tente novamente.'] });
    } finally {
      setSubmitting(false);
    }
  }

  const success = result && result.status === 'success';
  const errors = result && result.status === 'error' ? result.errors || [] : [];
  const closed = info && info.open === false;

  return (
    <div className="App">
      <SiteHeader />

      <main id="conteudo" className="tc">
        <div className="tc__shell">
          <section className="tc__intro">
            <span className="tc__eyebrow">Oportunidade freelancer</span>
            <h1 className="tc__title">Ajudante de produção</h1>
            <p className="tc__lead">
              Faça parte da nossa equipe de apoio em dias de maior demanda. Você informa quando
              pode trabalhar e nós entramos em contato conforme a necessidade da produção.
            </p>
          </section>

          {success ? (
            <div className="tc__alert tc__alert--success" role="status">
              <span className="tc__alert__title">Inscrição enviada com sucesso!</span>
              <p>
                Nossa equipe vai entrar em contato por telefone/WhatsApp para conversar sobre sua
                disponibilidade. Se der tudo certo, você recebe um novo link para completar o
                cadastro com seus documentos.
              </p>
            </div>
          ) : closed ? (
            <div className="tc__alert tc__alert--info" role="status">
              <span className="tc__alert__title">Inscrições fechadas no momento.</span>
              <p>
                Não estamos recebendo novos interessados agora. Fique de olho nas nossas redes
                sociais — avisamos por lá quando reabrirmos.
              </p>
            </div>
          ) : (
            <div className="tc__layout">
              <section className="tc__details">
                <div className="tc__banner"><span>Como funciona</span></div>

                <p className="tc__copy">
                  O trabalho é realizado por diária, de acordo com a demanda de cada semana — não
                  é uma vaga fixa.
                </p>

                {!loadingInfo && info && info.status !== 'error' && (
                  <div className="tc__facts">
                    <div className="tc__fact">
                      <div className="tc__diamond tc__diamond--verde-escuro">{FACT_ICONS.horario}</div>
                      <div className="tc__fact__body">
                        <span className="tc__fact__label">Horário</span>
                        <strong className="tc__fact__value">
                          das {info.work_hours.start} às {info.work_hours.end}
                        </strong>
                      </div>
                    </div>
                    <div className="tc__fact">
                      <div className="tc__diamond tc__diamond--verde-vivo">{FACT_ICONS.diaria}</div>
                      <div className="tc__fact__body">
                        <span className="tc__fact__label">Valor da diária</span>
                        <strong className="tc__fact__value">R$ {info.day_rate}</strong>
                      </div>
                    </div>
                    <div className="tc__fact">
                      <div className="tc__diamond tc__diamond--bordo">{FACT_ICONS.dias}</div>
                      <div className="tc__fact__body">
                        <span className="tc__fact__label">Dias possíveis</span>
                        <strong className="tc__fact__value">Segunda a sábado</strong>
                      </div>
                    </div>
                    <div className="tc__fact">
                      <div className="tc__diamond tc__diamond--verde-escuro">{FACT_ICONS.frequencia}</div>
                      <div className="tc__fact__body">
                        <span className="tc__fact__label">Frequência</span>
                        <strong className="tc__fact__value">Conforme demanda</strong>
                      </div>
                    </div>
                    <div className="tc__fact">
                      <div className="tc__diamond tc__diamond--azul-marinho">{FACT_ICONS.local}</div>
                      <div className="tc__fact__body">
                        <span className="tc__fact__label">Local</span>
                        <strong className="tc__fact__value">{info.location}</strong>
                      </div>
                    </div>
                  </div>
                )}

                <div className="tc__note">
                  <strong>Combinação prévia.</strong> Os dias são definidos ao final de cada
                  semana e combinados conforme a demanda e a sua disponibilidade. O pagamento é
                  feito toda sexta-feira, referente aos dias trabalhados, mediante RPA.
                </div>
              </section>

              <section className="tc__form-panel">
                <div className="tc__form-panel__heading">
                  <h2>Quero me inscrever</h2>
                  <p>Preencha seus dados para iniciarmos a conversa.</p>
                </div>

                {errors.length > 0 && (
                  <div className="tc__alert tc__alert--error" role="alert">
                    <span className="tc__alert__title">Não foi possível enviar.</span>
                    <p>{errors.join(' ')}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="name">
                      Nome completo <span className="tc__required">*</span>
                    </label>
                    <input
                      className="tc__input"
                      id="name"
                      type="text"
                      autoComplete="name"
                      maxLength={120}
                      placeholder="Digite seu nome e sobrenome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="tc__field">
                    <label className="tc__label" htmlFor="phone">
                      Telefone/WhatsApp <span className="tc__required">*</span>
                    </label>
                    <input
                      className="tc__input"
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={15}
                      placeholder="(00) 00000-0000"
                      value={phone}
                      onChange={(e) => setPhone(maskPhone(e.target.value))}
                      onBlur={() =>
                        setPhoneHelp(
                          phone && !isValidPhone(phone)
                            ? 'Telefone inválido — informe o DDD e o número (ex.: (11) 98765-4321).'
                            : 'Usaremos este número para falar sobre as próximas datas.'
                        )
                      }
                      required
                    />
                    <small className="tc__help">{phoneHelp}</small>
                  </div>

                  <div className="tc__field">
                    <label className="tc__label" htmlFor="email">
                      E-mail <span className="tc__required">*</span>
                    </label>
                    <input
                      className="tc__input"
                      id="email"
                      type="email"
                      autoComplete="email"
                      maxLength={255}
                      placeholder="voce@exemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <fieldset className="tc__fieldset">
                    <legend className="tc__legend">
                      Dias disponíveis para trabalhar <span className="tc__required">*</span>
                    </legend>
                    <div className="tc__days">
                      {!loadingInfo &&
                        info &&
                        info.weekdays &&
                        Object.entries(info.weekdays).map(([key, label]) => (
                          <div className="tc__day" key={key}>
                            <input
                              id={`day-${key}`}
                              type="checkbox"
                              checked={availability.includes(key)}
                              onChange={() => toggleDay(key)}
                            />
                            <label htmlFor={`day-${key}`}>{label}</label>
                          </div>
                        ))}
                    </div>
                    <small className="tc__help">Marque todos os dias em que normalmente está disponível.</small>
                  </fieldset>

                  <button className="tc__button" type="submit" disabled={submitting}>
                    {submitting ? 'Enviando...' : 'Quero me inscrever'}
                  </button>
                  <p className="tc__privacy">Ao enviar, você concorda em receber nosso contato sobre esta oportunidade.</p>
                </form>
              </section>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default TrabalheConosco;
